import React, { useState, useCallback, useEffect } from 'react';
import { kpiApi } from '../../services/api';
import { useDbNavigate } from '../../context/DatabaseContext';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { Tag, LayoutGrid, Wrench } from 'lucide-react';
import DashboardLayout from '../../components/common/DashboardLayout';
import DashboardTile from '../../components/common/DashboardTile';
import { PieTooltip } from '../../components/common/ChartTooltips';

const EquipmentDashboardPage = () => {
  const navigate = useDbNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kpis, setKpis] = useState(null);

  const loadKpis = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await kpiApi.getEquipment();
      setKpis(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load KPIs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handler = () => loadKpis();
    window.addEventListener('pmd-database-changed', handler);
    return () => window.removeEventListener('pmd-database-changed', handler);
  }, [loadKpis]);

  const editColors = {
    'ADD':     '#22c55e',
    'MOD':     '#3b82f6',
    'DEL':     '#ef4444',
    'FUT':     '#8b5cf6',
    'N/A':     '#94a3b8',
    'Unknown': '#e2e8f0'
  };

  const editClasses = {
    'ADD':     'stui-c-green',
    'MOD':     'stui-c-blue',
    'DEL':     'stui-c-red',
    'FUT':     'stui-c-violet',
    'N/A':     'stui-c-slate',
    'Unknown': 'stui-c-gray'
  };

  const oatColors = {
    'Yes':     '#22c55e',
    'No':      '#ef4444',
    'Partial': '#f59e0b',
    'Impact':  '#f97316',
    'N/A':     '#94a3b8',
    'Unknown': '#e2e8f0'
  };

  const oatClasses = {
    'Yes':     'stui-c-green',
    'No':      'stui-c-red',
    'Partial': 'stui-c-amber',
    'Impact':  'stui-c-orange',
    'N/A':     'stui-c-slate',
    'Unknown': 'stui-c-gray'
  };

  const palette = [
    '#3b82f6','#22c55e','#f59e0b','#ef4444','#8b5cf6',
    '#06b6d4','#f97316','#10b981','#6366f1','#ec4899',
    '#14b8a6','#84cc16','#a855f7','#0ea5e9','#d946ef'
  ];

  const paletteClasses = [
    'stui-c-blue','stui-c-green','stui-c-amber','stui-c-red','stui-c-violet',
    'stui-c-cyan','stui-c-orange','stui-c-emerald-light','stui-c-indigo','stui-c-pink',
    'stui-c-teal','stui-c-lime','stui-c-purple','stui-c-sky','stui-c-fuchsia'
  ];

  const toChartDataWithColors = (grouped, colorMap, classMap) => {
    if (!grouped || grouped.length === 0) return [];
    return grouped.filter(g => g.count > 0).map(g => ({
      name: g.key,
      value: g.count,
      fill: colorMap[g.key] || '#e2e8f0',
      colorClass: classMap ? (classMap[g.key] || 'stui-c-gray') : 'stui-c-gray'
    }));
  };

  const toChartDataPalette = (grouped) => {
    if (!grouped || grouped.length === 0) return [];
    return grouped.filter(g => g.count > 0).map((g, i) => ({
      name: g.key,
      value: g.count,
      fill: palette[i % palette.length],
      colorClass: paletteClasses[i % paletteClasses.length]
    }));
  };

  const MiniDonutCustom = ({ data, label }) => {
    const total = data.reduce((sum, d) => sum + d.value, 0);
    return (
      <div className="stui-dashboard-visual-section">
        <h4>{label}</h4>
        {data.length > 0 ? (
          <ResponsiveContainer width={90} height={90}>
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                cx="50%"
                cy="50%"
                innerRadius={24}
                outerRadius={40}
                paddingAngle={1}
                animationDuration={600}
              >
                {data.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} stroke="none" />
                ))}
              </Pie>
              <Tooltip content={<PieTooltip />} />
              <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central" className="stui-dashboard-visual-donut-label-sm">
                {total.toLocaleString()}
              </text>
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div className="stui-dashboard-visual-donut stui-empty stui-sm">
            <span className="stui-dashboard-visual-donut-center">0</span>
          </div>
        )}
        <div className="stui-dashboard-visual-legend">
          {data.map((g, i) => (
            <span key={i} className="stui-legend-item">
              <span className={`stui-legend-dot ${g.colorClass || ''}`} />
              {g.name}: {g.value.toLocaleString()}
            </span>
          ))}
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout
      id="equipment"
      title="Asset Dashboard"
      icon={Wrench}
      onRefresh={loadKpis}
      loading={loading}
      error={error}
    >
      <DashboardTile
        title="Tags"
        icon={Tag}
        onClick={() => navigate('/tags')}
        colSpan={3}
      >
        <div className="stui-dashboard-visual-chart-grid">
          <MiniDonutCustom data={toChartDataWithColors(kpis?.tagsByEdit, editColors, editClasses)} label="By Edit Type" />
          <MiniDonutCustom data={toChartDataWithColors(kpis?.tagsByOatComplete, oatColors, oatClasses)} label="OAT Complete" />
          <div className="stui-dashboard-visual-total">
            <span className="stui-dashboard-visual-total-value">{(kpis?.tags || 0).toLocaleString()}</span>
            <span className="stui-dashboard-visual-total-label">Total Tags</span>
          </div>
        </div>
      </DashboardTile>

      <DashboardTile
        title="Panels"
        icon={LayoutGrid}
        onClick={() => navigate('/panels')}
        colSpan={3}
      >
        <div className="stui-dashboard-visual-chart-grid">
          <MiniDonutCustom data={toChartDataWithColors(kpis?.panelsByEdit, editColors, editClasses)} label="By Edit Type" />
          <MiniDonutCustom data={toChartDataWithColors(kpis?.panelsByOatComplete, oatColors, oatClasses)} label="OAT Complete" />
          <MiniDonutCustom data={toChartDataPalette(kpis?.panelsByPanelType)} label="Panel Type" />
          <div className="stui-dashboard-visual-total">
            <span className="stui-dashboard-visual-total-value">{(kpis?.panels || 0).toLocaleString()}</span>
            <span className="stui-dashboard-visual-total-label">Total Panels</span>
          </div>
        </div>
      </DashboardTile>
    </DashboardLayout>
  );
};

export default EquipmentDashboardPage;
